import React from 'react';
import { useUpload } from '@/contexts/UploadContext';
import { Upload, CheckCircle, AlertCircle, Pause, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/shared/ui/badge';

const UploadStatusIndicator: React.FC = () => {
  const { getUploadStats, isUploading } = useUpload();
  const stats = getUploadStats();

  // Don't show indicator if no uploads
  if (stats.total === 0) {
    return null;
  }

  const getStatusIcon = () => {
    if (stats.uploading > 0) {
      return <Upload className="h-3 w-3 animate-pulse text-blue-500" />;
    }
    if (stats.error > 0) {
      return <AlertCircle className="h-3 w-3 text-red-500" />;
    }
    if (stats.paused > 0) {
      return <Pause className="h-3 w-3 text-yellow-500" />;
    }
    if (stats.completed > 0) {
      return <CheckCircle className="h-3 w-3 text-green-500" />;
    }
    return <Clock className="h-3 w-3 text-gray-500" />;
  };

  const getStatusText = () => {
    if (stats.uploading > 0) {
      return `Uploading ${stats.uploading}`;
    }
    if (stats.error > 0) {
      return `${stats.error} failed`;
    }
    if (stats.paused > 0) {
      return `${stats.paused} paused`;
    }
    if (stats.completed > 0) {
      return `${stats.completed} completed`;
    }
    return `${stats.pending} pending`;
  };

  const getStatusColor = () => {
    if (stats.uploading > 0) {
      return 'bg-blue-100 text-blue-800 border-blue-200';
    }
    if (stats.error > 0) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (stats.paused > 0) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (stats.completed > 0) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="px-3 py-2">
      <Badge 
        variant="outline" 
        className={cn(
          'w-full justify-start gap-2 text-xs',
          getStatusColor()
        )}
      >
        {getStatusIcon()}
        <span className="truncate">{getStatusText()}</span>
        {stats.total > 1 && (
          <span className="ml-auto text-xs opacity-70">
            {stats.total}
          </span>
        )}
      </Badge>
    </div>
  );
};

export default UploadStatusIndicator;
